import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import clsx from 'clsx';
import { ChevronRight, FileText, Loader2, MessageSquareText, MousePointerClick, Smartphone } from '@/components/icons';
import type { Project } from '@/lib/types';
import { RECORDER_MIN_VERSION, detectRecorder, startRecorder, versionAtLeast, type RecorderStartResult } from '@/lib/recorderBridge';
import { Dialog, useToast } from '@/components/ui';

/** What to tell the user when the recorder could not start; `again` names the button that starts it. */
export function startProblem(r: RecorderStartResult, again = 'Record in the browser'): ReactNode {
  switch (r.code) {
    case 'NOT_CONNECTED':
      return (
        <>
          The recorder isn't connected to this 1QA yet. It opens by itself - press <strong>Connect</strong>{' '}
          {r.address ? <>(the address {new URL(r.address).host} is filled in)</> : null}, then choose <strong>{again}</strong> again. If it
          didn't open, click the <strong>1QA Recorder</strong> icon in your browser toolbar.
        </>
      );
    case 'BUSY':
      return 'A recording is already running. Stop it with the bar on the recorded page first.';
    case 'UNSAVED':
      return (
        <>
          The recorder still has a recording you haven't saved. It opens so you can save or discard it (or click the <strong>1QA Recorder</strong> icon), then
          try again.
        </>
      );
    case 'SIGNED_OUT':
      return r.error ?? 'The recorder could not use your 1QA sign-in. Reload this page and try again.';
    case 'TIMEOUT':
      return 'The recorder did not answer. Reload this page and try again.';
    default:
      return r.error ?? 'The recorder could not start.';
  }
}

function Choice({
  icon,
  title,
  description,
  onClick,
  disabled,
  busy,
}: {
  icon: ReactNode;
  title: string;
  description: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  busy?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || busy}
      className={clsx(
        'group flex w-full items-center gap-4 rounded-card bg-surface p-4 text-left ring-1 ring-border ring-inset transition',
        'enabled:hover:bg-primary-subtle/40 enabled:hover:ring-primary',
        'disabled:cursor-not-allowed disabled:opacity-40',
      )}
    >
      <span className="grid size-11 shrink-0 place-items-center rounded-lg bg-primary-subtle text-primary">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-fg">{title}</span>
        <span className="mt-0.5 block text-xs text-fg-muted">{description}</span>
      </span>
      {busy ? (
        <Loader2 className="size-4 animate-spin text-primary" />
      ) : (
        <ChevronRight className="size-3 text-fg-muted transition group-enabled:group-hover:translate-x-0.5 group-enabled:group-hover:text-primary" />
      )}
    </button>
  );
}

/**
 * "New test" choices: record it with the browser recorder extension (sends you to the Browser recorder
 * page when it isn't installed), record it on a phone (Android projects, Device Agent), or write the steps as a
 * prompt in the test builder.
 */
export function NewTestChooser({ project, open, onClose }: { project: Project; open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<ReactNode>(null);
  const isWeb = project.platform === 'WEB';
  const isAndroid = project.platform === 'ANDROID';

  const close = () => {
    setProblem(null);
    onClose();
  };

  const record = async () => {
    setBusy(true);
    setProblem(null);
    try {
      const recorder = await detectRecorder();
      if (!recorder || !versionAtLeast(recorder.version, RECORDER_MIN_VERSION)) {
        const qs = new URLSearchParams({ project: project.id, ...(recorder ? { update: recorder.versionName } : {}) });
        navigate(`/helpers/recorder?${qs}`);
        return;
      }
      // Always a blank page: the user types the address to test, nothing is preloaded.
      const r = await startRecorder({ orgId: project.orgId, projectId: project.id, url: null });
      if (!r.ok) {
        setProblem(startProblem(r));
        return;
      }
      toast.success('Recording started in a new incognito window, signed out and clean. Type the address to test, use the app, then press Stop on the recording bar and save the test.');
      close();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} title="Create a test" description={`How do you want to create a test in ${project.name}?`}>
      <div className="space-y-3">
        {isAndroid && (
          <Choice
            icon={<Smartphone className="size-4" />}
            title="Record on your phone"
            description="Use the app on a real phone connected through the 1QA Device Agent; your taps, typing and scrolling become steps."
            onClick={() => navigate(`/projects/${project.id}/tests/record`)}
          />
        )}
        <Choice
          icon={<MousePointerClick className="size-4" />}
          title="Record in the browser"
          description={
            isWeb
              ? 'Opens a new incognito window (signed out, no saved data): type the address to test and use the app; your clicks and typing become steps. Needs the 1QA Recorder extension.'
              : 'Only for web app projects.'
          }
          onClick={record}
          disabled={!isWeb}
          busy={busy}
        />
        <Choice
          icon={<MessageSquareText className="size-4" />}
          title="Prompt automation"
          description="Describe the steps in plain English, for example “open the login page, sign in, check the dashboard”."
          onClick={() => navigate(`/projects/${project.id}/tests/new`)}
        />
        <Choice
          icon={<FileText className="size-4" />}
          title="From business documents"
          description="Upload a BRD, user stories or a specification (PDF, Word, text). The AI model finds the requirements and drafts positive, negative and edge-case tests for you to review."
          onClick={() => navigate(`/projects/${project.id}/requirements`)}
        />
        {problem && (
          <p
            role="alert"
            className="rounded-field bg-warning-subtle p-3 text-sm text-fg ring-1 ring-warning/40 ring-inset"
          >
            {problem}
          </p>
        )}
      </div>
    </Dialog>
  );
}
